'use strict';

import { updateSessionStoreValue } from '@core/utils/sessionStore';
import { goToDetailsView, goToReportDetails } from '@core/utils/navigate';
import { loadVideoWithPartialContentSupport } from '@core/utils/api';
import { isVideoItem } from '@core/utils/active';
import {
    isReportVideosList,
    isDetailLightboxView,
    isReportDetailsView,
} from '@core/utils/report';
import { cleanTextToCards } from '@core/utils/utils';
import { getGridLayoutWebComponent } from '@components/layouts/ResultsGridLayout/utils';
import {
    ACTIVE_ID_ATTRIBUTE,
    CURRENT_WESOG_PROVIDER_ATTRIBUTE,
    DETAILS_VIEW_ATTRIBUTE_IMG,
    DATA_ID_ATTRIBUTE,
    ADD_COLLECTION_METHOD_TYPE_ATTRIBUTE,
} from '@core/constants/ATTRIBUTES';
import {
    REMOVE_ACTIVE_ITEM,
    LIGHTBOX_ITEM_TYPE_ATTRIBUTE,
} from '@core/constants/LIGHTBOX';
import { PROV_FILTERS } from '@core/constants/API';
import { WESOG_SESSION_STORE_PARAMS } from '@core/constants/STORE';
import { ADD_CART_ELEMENT_TYPE } from '@core/constants/CONSTANTS';
import { SERVICE_WORKER_VERSION } from '@core/constants/SERVICE-WORKER';

import {
    ZOOM_MODAL_ATTRIBUTE,
    CUSTOM_COMMON_MODAL_WEB_COMPONENT_ATTRIBUTE,
    CUSTOM_ZOOM_MODAL_LOADING_SPINNER_WEB_COMPONENT,
} from './CONSTANTS';

/**
 * Handle the next and previous image navigation.
 * @param {Object} params - The parameters object
 * @param {HTMLElement} params.shadowRoot - The shadow root of the component
 * @param {string} params.preActiveId - The previous active ID
 * @param {string} params.posActiveId - The next active ID
 * @param {function} params.onNavigation - Callback function when navigation occurs
 * @returns {void}
 */
export function handleNextPrevImage({
    shadowRoot,
    preActiveId,
    posActiveId,
    onNavigation,
    firstRender,
}) {
    const commonCode = (nextId) => {
        onNavigation(nextId);
    };

    if (firstRender) {
        return;
    }

    shadowRoot.querySelector('[data-slides-arrow-left]').addEventListener('click', () => {
        commonCode(preActiveId);
    });

    shadowRoot
        .querySelector('[data-slides-arrow-right]')
        .addEventListener('click', () => {
            commonCode(posActiveId);
        });
}

/**
 * Handle the see more button click.
 * @param {HTMLElement} shadowRoot - The shadow root of the component
 * @param {object} active - The active item data
 * @returns {void}
 */
export function handleSeeMoreButton(shadowRoot, active, firstRender = false) {
    if (firstRender) {
        return;
    }

    shadowRoot.querySelector('[data-see-more-button]').addEventListener('click', () => {
        const img_uid = active.uid || active.id;

        updateSessionStoreValue(WESOG_SESSION_STORE_PARAMS.CURRENT_SELECTED_IMAGE, {
            uid: img_uid,
            ...active,
        });

        setTimeout(() => {
            goToDetailsView(img_uid);
        }, 500);
    });
}

/**
 * Handle the see report button click.
 * @param {HTMLElement} shadowRoot - The shadow root of the component
 * @param {object} active - The active item data
 * @returns {void}
 */
export function handleSeeReportButton(shadowRoot, active, firstRender = false) {
    if (firstRender) {
        return;
    }

    shadowRoot.querySelector('[data-see-report-button]').addEventListener('click', () => {
        goToReportDetails(active.id_event);
    });
}

/**
 * Hide unnecessary icons for lightbox view.
 * @param {HTMLElement} shadowRoot - The shadow root of the component
 * @returns {void}
 */
export function hideUnnecessaryIconsForLightbox(shadowRoot) {
    if (!isDetailLightboxView()) {
        shadowRoot.querySelector('[data-star-icon]').style.display = 'none';
        shadowRoot.querySelector('[data-delete-icon]').style.display = 'none';
    }
}

/**
 * Override the common custom modal container style.
 * @param {HTMLElement} shadowRoot - The shadow root of the component
 * @returns {void}
 */
export function overrideCommonCustomModalContainerStyle(shadowRoot) {
    setTimeout(() => {
        const customCommonModalContent = shadowRoot
            .querySelector(CUSTOM_COMMON_MODAL_WEB_COMPONENT_ATTRIBUTE)
            .shadowRoot.querySelector('.modal-content');

        customCommonModalContent.style.maxWidth = '90vw';
        customCommonModalContent.style.width = '65%';
        customCommonModalContent.style.height = '90%';
        customCommonModalContent.style.maxHeight = '84vh';
    }, 0);
}

/**
 * Get the grid container element.
 * @returns {HTMLElement}
 */
export function getGridContainer() {
    return getGridLayoutWebComponent().shadowRoot.querySelector('[data-grid-container]');
}

/**
 * Handle the active images navigation.
 * @param {string} imageId - The current image ID
 * @returns {{
 *  active: object,
 *  preActiveId: string,
 *  posActiveId: string
 * }} Object containing active, preActiveId, and posActiveId
 */
export function handleActivesImages(imageId) {
    const gridContainer = getGridContainer();

    const activateElement = gridContainer.querySelector(
        `[${DATA_ID_ATTRIBUTE}="${imageId}"]`,
    );
    const active = activateElement
        ? JSON.parse(activateElement.getAttribute(DETAILS_VIEW_ATTRIBUTE_IMG))
        : {};

    const preActiveElement = activateElement?.previousElementSibling;
    const preActiveId = preActiveElement?.getAttribute(DATA_ID_ATTRIBUTE);

    const posActiveElement = activateElement?.nextElementSibling;
    const posActiveId = posActiveElement?.getAttribute(DATA_ID_ATTRIBUTE);

    return { active, preActiveId, posActiveId };
}

/**
 * Assign the active ID to the elements.
 * @param {HTMLElement} shadowRoot - The shadow root of the component
 * @param {string} imageId - The image ID
 * @param {object} active - The active item data
 * @returns {void}
 */
export function assignActiveIdToElements(shadowRoot, imageId, active) {
    const downloadIcon = shadowRoot.querySelector('[data-download-icon]');
    const imgLightboxIcon = shadowRoot.querySelector('[data-add-lightbox-icon]');

    setTimeout(() => {
        [
            shadowRoot.querySelector('[data-add-cart-icon]'),
            shadowRoot.querySelector('[data-delete-icon]'),
            imgLightboxIcon,
        ].forEach((element) => {
            element.setAttribute(ACTIVE_ID_ATTRIBUTE, imageId);
        });

        [
            shadowRoot.querySelector('[data-add-cart-icon]'),
            shadowRoot.querySelector('[data-delete-icon]'),
            downloadIcon,
            imgLightboxIcon,
        ].forEach((element) => {
            element.setAttribute(ACTIVE_ID_ATTRIBUTE, imageId);
        });

        downloadIcon.setAttribute(
            CURRENT_WESOG_PROVIDER_ATTRIBUTE,
            active?.wesog_provider || PROV_FILTERS.GTRES,
        );
        downloadIcon.setAttribute(ZOOM_MODAL_ATTRIBUTE, 'true');

        if (isReportVideosList()) {
            downloadIcon.setAttribute(
                ADD_COLLECTION_METHOD_TYPE_ATTRIBUTE,
                active && active.elements_event === 1
                    ? ADD_CART_ELEMENT_TYPE.ELEMENT
                    : ADD_CART_ELEMENT_TYPE.REPORT,
            );
            downloadIcon.setAttribute(
                ACTIVE_ID_ATTRIBUTE,
                active && active.elements_event === 1 ? active.uid : imageId,
            );
        } else {
            downloadIcon.setAttribute(ACTIVE_ID_ATTRIBUTE, imageId);
        }

        imgLightboxIcon.setAttribute(
            DETAILS_VIEW_ATTRIBUTE_IMG,
            JSON.stringify({
                caption: active ? active.caption : '',
                img_src: active ? active.img_src : '',
            }),
        );
    }, 0);
}

/**
 * Reproduce the video automatically.
 * @param {HTMLElement} shadowRoot - The shadow root of the component
 * @param {object} active - The active item data
 * @param {HTMLVideoElement} videoElement - The video element
 * @param {function} fetchVideo - The fetch function
 * @returns {Promise<void>}
 */
export async function reproduceVideo(shadowRoot, active, videoElement, fetchVideo) {
    try {
        shadowRoot.querySelector(
            CUSTOM_ZOOM_MODAL_LOADING_SPINNER_WEB_COMPONENT,
        ).style.display = 'flex';

        let videoUrl;

        const cache = await caches.open(SERVICE_WORKER_VERSION);
        const cachedResponse = await cache.match(active.img_zoom);

        if (cachedResponse) {
            videoUrl = URL.createObjectURL(await cachedResponse.blob());
        } else {
            const response = await loadVideoWithPartialContentSupport(
                fetchVideo,
                active.img_zoom,
            );

            const responseClone = response.clone();

            videoUrl = URL.createObjectURL(await response.blob());
            await cache.put(active.img_zoom, responseClone);
        }

        videoElement.querySelector('source').setAttribute('src', videoUrl);
        videoElement.load();

        if (videoElement.paused) {
            await videoElement.play();
        }

        shadowRoot.querySelector(
            CUSTOM_ZOOM_MODAL_LOADING_SPINNER_WEB_COMPONENT,
        ).style.display = 'none';
    } catch (error) {
        console.error('Error loading video', error);
    }
}

/**
 * Stop the video reproduction.
 * @param {HTMLVideoElement} videoElement - The video element
 * @param {function} fetchVideo - The fetch function
 * @returns {void}
 */
export function stopVideo(videoElement, fetchVideo) {
    if (videoElement) {
        videoElement.pause();
        videoElement.currentTime = 0;
        videoElement.querySelector('source').setAttribute('src', '');
    }

    if (fetchVideo && typeof fetchVideo.abort === 'function') {
        fetchVideo.abort();
    }
}

/**
 * Render the image content.
 * @param {HTMLElement} shadowRoot - The shadow root of the component
 * @param {object} active - The active item data
 * @param {HTMLVideoElement} videoElement - The video element
 * @param {function} reproduceVideo - Function to reproduce video
 * @returns {void}
 */
export function renderImageContent(shadowRoot, active, videoElement, reproduceVideo) {
    const detailsViewImageImage = shadowRoot.querySelector(
        '[data-details-view-image-image]',
    );
    const imageElement = detailsViewImageImage.querySelector('img');

    imageElement.src = active.img_zoom || active.img_src;
    imageElement.alt = active.caption;

    shadowRoot.querySelector(
        '.common-details-view-image-info [data-details-view-image-info-title]',
    ).innerHTML = `${active.reference || ''}`;

    const imageInfoCaption = shadowRoot.querySelector(
        '[data-details-view-image-info-caption]',
    );
    imageInfoCaption.innerHTML = active.caption;

    shadowRoot.querySelector('[data-details-view-image-info-date]').innerHTML =
        `${active.date || ''}`;

    const imageInfoTitle = shadowRoot.querySelector(
        '[data-details-view-image-info-title-event]',
    );

    if (isVideoItem(active.img_zoom)) {
        detailsViewImageImage.style.height = '50%';
        videoElement = detailsViewImageImage.querySelector('video');

        videoElement.setAttribute('poster', active.img_src);
        imageElement.style.display = 'none';
        videoElement.style.display = 'block';

        reproduceVideo(shadowRoot, active, videoElement);

        const [videoCaptionText, videoCaptionTextDescription] = active.caption
            ? active.caption.split('|')
            : ['', ''];

        if (active.title_event || videoCaptionTextDescription) {
            imageInfoTitle.style.display = '-webkit-box';
            shadowRoot.querySelector('video').style.maxHeight = '50vh';

            imageInfoTitle.innerHTML = cleanTextToCards(active.title_event);
            imageInfoCaption.innerHTML = cleanTextToCards(active.caption);

            if (videoCaptionTextDescription) {
                imageInfoTitle.innerHTML = cleanTextToCards(videoCaptionText);
                imageInfoCaption.innerHTML = cleanTextToCards(
                    videoCaptionTextDescription,
                );
            }
        } else {
            imageInfoTitle.style.display = 'none';
            shadowRoot.querySelector('video').style.maxHeight = '54vh';
        }
    } else {
        shadowRoot.querySelector(
            CUSTOM_ZOOM_MODAL_LOADING_SPINNER_WEB_COMPONENT,
        ).style.display = 'none';
        detailsViewImageImage.style.height = '100%';

        if (videoElement) {
            videoElement.style.display = 'none';
            imageElement.style.display = 'block';
        }

        if (active.title_event) {
            imageInfoTitle.style.display = '-webkit-box';
            imageInfoTitle.innerHTML = cleanTextToCards(active.title_event);
        } else {
            imageInfoTitle.style.display = 'none';
        }
    }
}

/**
 * Render the navigation arrows.
 * @param {HTMLElement} shadowRoot - The shadow root of the component
 * @param {string} preActiveId - The previous active ID
 * @param {string} posActiveId - The next active ID
 * @returns {void}
 */
export function renderNavigationArrows(shadowRoot, preActiveId, posActiveId) {
    if (!preActiveId) {
        shadowRoot.querySelector('[data-slides-arrow-left]').style.display = 'none';
    } else {
        shadowRoot.querySelector('[data-slides-arrow-left]').style.display = 'block';
    }

    if (!posActiveId) {
        shadowRoot.querySelector('[data-slides-arrow-right]').style.display = 'none';
    } else {
        shadowRoot.querySelector('[data-slides-arrow-right]').style.display = 'block';
    }
}

/**
 * Render the action buttons visibility.
 * @param {HTMLElement} shadowRoot - The shadow root of the component
 * @param {object} active - The active item data
 * @returns {void}
 */
export function renderActionButtons(shadowRoot, active) {
    const starIcon = shadowRoot.querySelector('[data-star-icon]');
    const seeReportButton = shadowRoot.querySelector('[data-see-report-button]');
    const addCartIcon = shadowRoot.querySelector('[data-add-cart-icon]');
    const addLightboxIcon = shadowRoot.querySelector('[data-add-lightbox-icon]');
    const deleteIcon = shadowRoot.querySelector('[data-delete-icon]');

    starIcon.setAttribute(DETAILS_VIEW_ATTRIBUTE_IMG, JSON.stringify(active));

    if (active.wesog_provider === PROV_FILTERS.GTRES) {
        [addCartIcon, addLightboxIcon, deleteIcon, starIcon].forEach((element) => {
            element.style.display = 'block';
        });
        hideUnnecessaryIconsForLightbox(shadowRoot);
    } else {
        [addCartIcon, addLightboxIcon, deleteIcon, starIcon].forEach((element) => {
            element.style.display = 'none';
        });
    }

    if (active.id_event && !isReportDetailsView()) {
        if (isVideoItem(active.img_zoom)) {
            if (active.elements_event && active.elements_event > 1) {
                seeReportButton.style.display = 'block';
            } else {
                seeReportButton.style.display = 'none';
            }
        } else {
            seeReportButton.style.display = 'block';
        }
    } else {
        seeReportButton.style.display = 'none';
    }
}

/**
 * Set up the delete icon attributes.
 * @param {HTMLElement} shadowRoot - The shadow root of the component
 * @returns {void}
 */
export function setupDeleteIcon(shadowRoot) {
    const deleteIcon = shadowRoot.querySelector('[data-delete-icon]');
    deleteIcon.setAttribute(
        LIGHTBOX_ITEM_TYPE_ATTRIBUTE,
        REMOVE_ACTIVE_ITEM.LIGHTBOX_IMG,
    );
}

/**
 * Clear download options.
 * @param {HTMLElement} shadowRoot - The shadow root of the component
 * @returns {void}
 */
export function clearDownloadOptions(shadowRoot) {
    if (!shadowRoot.querySelector('.options-download-modal ul').innerHTML) {
        shadowRoot.querySelector('.options-download-modal ul').innerHTML = '';
        shadowRoot.querySelector('.options-download-modal').style.display = 'none';
    }
}
